import { DownloadOutlined } from "@ant-design/icons";
import { PageHeader } from "@ant-design/pro-components";
import { Alert, Button, Card, Descriptions, Form, InputNumber, Modal, Space, Spin, message } from "antd";
import { memo, useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../../api/api";
import JalaliDateCell from "../../../Components/Leads/JalaliDateCell";
import { getLeadOwnerLabel } from "../../../Util/GetLeadOwnerLabel";
import exportDate from "./exportDate";
import CheckPermission from "../../../Util/CheckPermission";
import styles from "./LeadExportDetails.module.css";
import LeadExportBatches from "./LeadExportBatches";

const renderDate = (value) => (
  <span dir="ltr">{exportDate(value)?.format("YYYY/MM/DD") || "—"}</span>
);

const LeadExportDetails = ({ canView, canReceiveLeads, canListBatches, canExport }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [form] = Form.useForm();
  const [receiving, setReceiving] = useState(false);
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [receiveError, setReceiveError] = useState("");

  const receiveLeads = async () => {
    if (receiving) return;
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setReceiving(true);
    setReceiveError("");
    try {
      const res = await api.post(`/lead-exports/${id}/receive-leads`, { data: { number: values.number } });
      const result = res.data.data;
      setRecord((previous) => ({ ...previous, items_count: result.items_count }));
      setReceiveModalOpen(false);
      if (result.added_count === 0) message.info("لید واجد شرایطی برای این خروجی پیدا نشد.");
      else message.success(`${result.added_count} لید به خروجی اضافه شد.`);
    } catch (requestError) {
      setReceiveError(requestError.response?.data?.error?.message || "خطا در تخصیص لید");
    } finally {
      setReceiving(false);
    }
  };

  useEffect(() => {
    if (!canView) return;
    let active = true;
    setLoading(true);
    setError("");
    setRecord(null);
    const loadDetails = async () => {
      try {
        const res = await api.get(`/lead-exports/${id}`);
        if (!active) return;
        const data = res.data?.data;
        if (data) setRecord({ id: data.id, ...data.attributes });
        else setError("خروجی لید پیدا نشد.");
      } catch (requestError) {
        if (active) setError(requestError.response?.status === 404
          ? "خروجی لید پیدا نشد."
          : requestError.response?.data?.error?.message || "خطا در دریافت جزئیات خروجی لید");
      } finally {
        if (active) setLoading(false);
      }
    };
    loadDetails();
    return () => { active = false; };
  }, [id, canView, retry]);

  if (!canView) return <Alert type="error" message="شما دسترسی به جزئیات خروجی لید ندارید." />;

  return (
    <div className={styles.page}>
      <PageHeader
        title={record?.title || "جزئیات خروجی لید"}
        avatar={{ icon: <DownloadOutlined />, size: "large" }}
        extra={<Space>
          {canReceiveLeads && record && !loading && <Button type="primary" onClick={() => {
            form.resetFields();
            setReceiveError("");
            setReceiveModalOpen(true);
          }}>تخصیص لید</Button>}
          <Button onClick={() => navigate("/lead-exports")}>بازگشت به خروجی‌های لید</Button>
        </Space>}
      />
      <Spin spinning={loading}>
        {error && <Alert type="error" message={error} action={<Button onClick={() => setRetry((value) => value + 1)}>تلاش مجدد</Button>} />}
        {!loading && record && (
          <Card title="اطلاعات خروجی">
            <Descriptions bordered size="small" column={{ xs: 1, sm: 1, md: 2 }}>
              <Descriptions.Item label="عنوان">{record.title}</Descriptions.Item>
              <Descriptions.Item label="مالک لید">{getLeadOwnerLabel(record.lead_owner)}</Descriptions.Item>
              <Descriptions.Item label="از تاریخ">{renderDate(record.from_date)}</Descriptions.Item>
              <Descriptions.Item label="تا تاریخ">{renderDate(record.to_date)}</Descriptions.Item>
              <Descriptions.Item label="زمان ایجاد"><JalaliDateCell date={record.createdAt} fallback="—" /></Descriptions.Item>
              <Descriptions.Item label="آخرین ویرایش"><JalaliDateCell date={record.updatedAt} fallback="—" /></Descriptions.Item>
              <Descriptions.Item label="تعداد لیدهای خروجی" span={2}>
                {record.items_count ?? "—"}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}
      </Spin>
      {!loading && record && canListBatches && <LeadExportBatches
        key={id}
        id={id}
        title={record.title}
        refreshKey={record.items_count}
        canExport={canExport}
      />}
      <Modal
        title="تخصیص لید"
        open={receiveModalOpen}
        confirmLoading={receiving}
        okText="تخصیص"
        cancelText="انصراف"
        onOk={receiveLeads}
        onCancel={() => { if (!receiving) setReceiveModalOpen(false); }}
        maskClosable={!receiving}
        closable={!receiving}
        forceRender
      >
        {receiveError && <Alert type="error" message={receiveError} />}
        <Form form={form} layout="vertical" disabled={receiving}>
          <Form.Item name="number" label="تعداد لید" rules={[
            { required: true, message: "تعداد لید را وارد کنید." },
            { validator: (_, value) => value == null || (Number.isSafeInteger(value) && value > 0)
              ? Promise.resolve() : Promise.reject(new Error("تعداد لید باید یک عدد صحیح مثبت باشد.")) },
          ]}>
            <InputNumber min={1} step={1} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default connect((state) => {
  const permissions = state.userData?.permissions || [];
  return {
    canView: CheckPermission("lead_export_get_details", permissions),
    canReceiveLeads: CheckPermission("lead_export_receive_leads", permissions),
    canListBatches: CheckPermission("lead_export_item_list_batches", permissions),
    canExport: CheckPermission("lead_export_item_get_batch", permissions),
  };
})(memo(LeadExportDetails));
